import { Icon } from '@chakra-ui/icon'
import { Box, Flex, Text } from '@chakra-ui/layout'
import React, { FC } from 'react'
import IconArrowRight from '~/assets/svgs/icon-arrow-right.svg?react'
import IconCallIn from '~/assets/svgs/icon-call-in.svg?react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconFax from '~/assets/svgs/icon-fax.svg?react'
import IconMessaging from '~/assets/svgs/icon-messaging.svg?react'
import IconVoicemail from '~/assets/svgs/icon-voicemail.svg?react'
import {
  EventHistory,
  EventHistoryStep,
} from '~/components/shared/EventHistory'
import { formatPhoneNumber } from '~/utils/formatter'

import { toNounCasing } from '@mangovoice/core'
import { formatDateString, formatTimeString } from '@mangovoice/core'

import { useContactEventStyle } from './componentStyles/ContactEvent.style'
import { useContactHistoryStyle } from './componentStyles/ContactHistory.style'
import {
  ContactHistory,
  ContactHistoryType,
} from './viewModels/Contacts.viewmodel'

const historyTypeToIconMap: Record<
  ContactHistoryType,
  FC<React.SVGProps<SVGSVGElement>>
> = {
  messaging: IconMessaging,
  inboundCall: IconCallIn,
  outboundCall: IconCallStart,
  inboundFax: IconFax,
  outboundFax: IconFax,
  voicemail: IconVoicemail,
}
const historyTypeToLabelMap: Record<ContactHistoryType, string> = {
  messaging: 'Message Thread',
  inboundCall: 'Inbound Call',
  outboundCall: 'Outbound Call',
  inboundFax: 'Fax Received',
  outboundFax: 'Fax Sent',
  voicemail: 'Voicemail',
}

interface ContactEventProps {
  eventDate: Date
  icon: FC<React.SVGProps<SVGSVGElement>>
  label: string
  fromNumber?: string
  toNumber?: string
}
const ContactEvent = ({
  eventDate,
  icon,
  label,
  fromNumber,
  toNumber,
}: ContactEventProps) => {
  const styles = useContactEventStyle()
  return (
    <Flex sx={styles.container}>
      <Text
        sx={styles.dateText}
      >{`${formatDateString(eventDate)} ${formatTimeString(eventDate)}`}</Text>
      <Flex sx={styles.typeContainer}>
        <Icon sx={styles.typeIcon} as={icon} />
        <Text sx={styles.typeText}>{label}</Text>
      </Flex>
      <Flex sx={styles.numbersContainer}>
        {fromNumber && (
          <>
            <Text sx={styles.fromNumberText}>{fromNumber}</Text>
            <Icon as={IconArrowRight} sx={styles.arrowIcon} />
          </>
        )}
        {toNumber && <Text sx={styles.toNumberText}>{toNumber}</Text>}
      </Flex>
    </Flex>
  )
}

interface ContactHistoryProps {
  contactHistory: ContactHistory[]
}
export const ContactEventHistory = ({
  contactHistory,
}: ContactHistoryProps) => {
  const styles = useContactHistoryStyle()
  return (
    <Flex sx={styles.container}>
      {/* Contact History Header */}
      <Flex sx={styles.headerContainer}>
        <Text sx={styles.headerText}>Recent Activity</Text>
        <Text sx={styles.timespanText}>Last 14 Days</Text>
      </Flex>
      <Box sx={styles.scrollContainer} className="scrollbar-hidden">
        <EventHistory sx={styles.stepperContainer}>
          {contactHistory.map((history, index) => {
            let fromNumberDisplay: string | undefined
            let toNumberDisplay: string | undefined
            if (history?.fromNumber) {
              if (typeof history?.fromNumber === 'string') {
                fromNumberDisplay = formatPhoneNumber(history.fromNumber)
              } else {
                fromNumberDisplay = `${toNounCasing(
                  history.fromNumber.label,
                )}: ${formatPhoneNumber(history.fromNumber.number)}`
              }
            }
            if (history?.toNumber) {
              if (typeof history?.toNumber === 'string') {
                toNumberDisplay = formatPhoneNumber(history.toNumber)
              } else {
                toNumberDisplay = `${toNounCasing(
                  history.toNumber.label,
                )}: ${formatPhoneNumber(history.toNumber.number)}`
              }
            }

            return (
              <EventHistoryStep key={index}>
                <ContactEvent
                  eventDate={history.date}
                  icon={historyTypeToIconMap[history.type]}
                  label={historyTypeToLabelMap[history.type]}
                  fromNumber={fromNumberDisplay}
                  toNumber={toNumberDisplay}
                />
              </EventHistoryStep>
            )
          })}
        </EventHistory>
      </Box>
    </Flex>
  )
}
